import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import debounce from 'lodash.debounce'
import axios from '../../../../../lib/axios'
import theme from 'theme'

/* Utils */
import { SSMAssetType, SSMNistPillar, Vendor } from '../../../../../models'
import { DrawerProps } from '../../SSM'

/* Components */
import {
  AutocompleteContainer,
  ResultListItem,
  ResultsList,
  SearchVendorsContainer,
  SearchVendorsInputWrapper,
  SearchVendorsResultsContainer
} from './searchCustomVendors.styles'
import ThemeButton from '../../../../components/buttons/primary/ThemeButton'
import { SSMButtonAddIcon } from '../../../../components/svg'
import { IntegrationLogo, NoIntegrationLogo } from '../../securityStackMap.styles'
import CloseIcon from '@mui/icons-material/Close'

interface Props {
  nist: SSMNistPillar
  asset: SSMAssetType
  handleAdd: (vendorId: string, nist: SSMNistPillar, asset: SSMAssetType) => void
  customVendorOptions: Vendor[]
  setCustomVendorOptions: Dispatch<SetStateAction<Vendor[]>>
  drawerProps: DrawerProps
  value: string
  setValue: Dispatch<SetStateAction<string>>
  setVendorId: Dispatch<SetStateAction<string>>
}

const SearchCustomVendors: FC<Props> = ({
  customVendorOptions,
  setCustomVendorOptions,
  nist,
  asset,
  handleAdd,
  drawerProps,
  value,
  setValue,
  setVendorId
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<Vendor[]>(customVendorOptions)

  // Sync options when customVendorOptions changes (e.g., when cleared on drawer close)
  useEffect(() => {
    setOptions(customVendorOptions)
  }, [customVendorOptions])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSubmitting(true)
    setValue(e.target.value)
  }

  const handleSearchVendors = async (newValue?: string | undefined): Promise<void> => {
    setLoading(true)
    const val = newValue ?? value
    setValue(val)

    if (!val) {
      setValue('')
      setCustomVendorOptions([])
      setLoading(false)

      return
    }
    if (val.length < 2) return

    const newVal = val.includes('https://') ? val.replace('https://', '') : val
    try {
      const res = await axios.get<{ vendor: Vendor }>(`/api/v3/vendors_products/ext/search?domain=${newVal}`)

      const vendorId = res.data.vendor.uuid
      const drawerItemIds = drawerProps.assets.map((n) => n.uuid).filter((n) => typeof n === 'string')

      if (res.status === 200 && !drawerItemIds.includes(vendorId)) {
        const foundVendor = res.data.vendor
        setCustomVendorOptions([foundVendor])
        setOptions([foundVendor])
      }
    } catch (e) {
      console.error('Failed searching vendors', e)
      setCustomVendorOptions([])
      setOptions([])
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  const debouncedSearch = useMemo(() => debounce(handleSearchVendors, 200), [value])

  return (
    <SearchVendorsContainer sx={{ marginTop: '0 !important' }}>
      <AutocompleteContainer>
        <SearchVendorsInputWrapper>
          <TextField
            variant="outlined"
            color="primary"
            label="Website"
            size="medium"
            placeholder="Paste URL"
            value={value}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              autoComplete: 'off',
              onKeyDown: (e) => {
                if (e.keyCode === 13) {
                  const newValue = (e as any).target.value || ''
                  setValue(newValue)
                  debouncedSearch(newValue)
                }
              },
              endAdornment: value.length > 0 && (
                <IconButton
                  onClick={() => {
                    setOptions([])
                    setCustomVendorOptions([])
                    setValue('')
                    setVendorId('')
                  }}
                  className="end-adornment-close-icon"
                >
                  <CloseIcon width={24} />
                </IconButton>
              )
            }}
          />
        </SearchVendorsInputWrapper>
        {/* Loading */}
        {loading && (
          <SearchVendorsResultsContainer>
            <Box className="search-vendors-loading-wrapper">
              <CircularProgress size={48} className="search-vendors-loading-progress" />
            </Box>
          </SearchVendorsResultsContainer>
        )}
        {/* No results found */}
        {value.length > 0 && !options.length && !loading && !submitting && (
          <SearchVendorsResultsContainer>
            <Box className="search-vendors-empty-wrapper">
              <Typography className="search-vendors-empty-title">Vendor Not Found</Typography>
              <Typography className="search-vendors-empty-text">
                Make sure your spelling is correct, and enter the address without 'http://' or a trailing '/'
              </Typography>
            </Box>
          </SearchVendorsResultsContainer>
        )}
        {/* Results */}
        {(options.length === 1 &&
          (options as Vendor[])[0].display_name === '' &&
          (options as Vendor[])[0].name === '') ||
          (options.length > 0 && !loading && (
            <SearchVendorsResultsContainer>
              <Typography className="search-vendors-results-text">Top Matches</Typography>
              <ResultsList length={options.length}>
                {(options as Vendor[]).map((option, index) => {
                  if (option.display_name === '' && option.name === '') return null

                  return (
                    <ResultListItem iscustomvendor="true" key={index}>
                      {option.logo_path && option.logo_path.length > 0 ? (
                        <IntegrationLogo
                          logo={`${process.env.REACT_APP_LOGO_HOST}/${option.logo_path}`}
                          sx={{
                            width: '24px !important',
                            height: '24px !important',
                            marginRight: `${theme.spacing.small}px`
                          }}
                        />
                      ) : (
                        <NoIntegrationLogo
                          sx={{
                            width: '24px !important',
                            height: '24px !important',
                            marginRight: `${theme.spacing.small}px`
                          }}
                        >
                          <Typography className="no-integration-logo-letter">{option.display_name[0]}</Typography>
                        </NoIntegrationLogo>
                      )}
                      <Typography className="option-text">{option.display_name}</Typography>

                      <ThemeButton
                        themevariant="tertiary"
                        sx={{ padding: `${theme.spacing.sm}px ${theme.spacing.md}px !important`, minWidth: '100px' }}
                        onClick={() => {
                          handleAdd(option.uuid, nist, asset)
                          setOptions([])
                          setValue(option.display_name)
                          setSubmitting(true)
                        }}
                      >
                        <>
                          <SSMButtonAddIcon />
                          Add
                        </>
                      </ThemeButton>
                    </ResultListItem>
                  )
                })}
              </ResultsList>
            </SearchVendorsResultsContainer>
          ))}
      </AutocompleteContainer>
      <IconButton onClick={() => debouncedSearch()} className="search-icon-wrapper">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17.5 17.5L13.1694 13.1694M13.1694 13.1694C14.3004 12.0384 15 10.4759 15 8.75C15 5.29822 12.2018 2.5 8.75 2.5C5.29822 2.5 2.5 5.29822 2.5 8.75C2.5 12.2018 5.29822 15 8.75 15C10.4759 15 12.0384 14.3004 13.1694 13.1694Z"
            stroke="#1E1A1D"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </IconButton>
    </SearchVendorsContainer>
  )
}

export default SearchCustomVendors
